import React from 'react';
import { Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';
import 'react-ladda-button/dist/ladda-themeless.min.css';
import { Comment } from '../interfaces/Comment';
import { Dropdown } from 'rsuite';
import i18n from 'i18next';
import './comments.scss';
import { FormInput } from '../components';
import { camelCaseToText } from '../utils/camelCaseToText';
import { Task } from '../interfaces/Task';
import { User } from '../interfaces/User';
import MentionTextarea from './MentionTextarea';

interface CommentsProps {
    comments: Comment[];
    newComment: string;
    isCopied: boolean;
    textAreaRef: any;
    topics: string[];
    moduleFilter: string;
    sortBy: string;
    topic: string;
    users: User[];
    commentTextChange: (text: string, mentionedUsers: User[]) => void;
    addCommentClicked: () => void;
    onDeleteComment: (id: string) => void;
    handleModuleFilterChange: (eventKey: any) => void;
    handleSortByChange: (eventKey: any) => void;
    onCopyComment: (comment: Comment) => void;
    getBadgeClass: (comment: Comment) => any;
    onSelected: (value: string) => void;
}

const Comments = ({
    comments,
    newComment,
    isCopied,
    textAreaRef,
    topics,
    moduleFilter,
    sortBy,
    topic,
    users,
    commentTextChange,
    addCommentClicked,
    onDeleteComment,
    handleModuleFilterChange,
    handleSortByChange,
    onCopyComment,
    getBadgeClass,
    onSelected,
}: CommentsProps) => {
    const renderTextWithMentions = (text: string, mentionedUsers: User[] = []) => {
        if (!mentionedUsers || mentionedUsers.length === 0) {
            return text;
        }

        // Build a regex pattern that matches any of the mentioned user names
        const mentionPatterns = mentionedUsers
            .filter((u) => u.fullName)
            .map((u) => `@${u.fullName!.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`)
            .join('|');

        if (!mentionPatterns) {
            return text;
        }

        const regex = new RegExp(`(${mentionPatterns})`, 'g');
        const parts = text.split(regex);

        return parts.map((part, index) => {
            if (part.startsWith('@')) {
                const userName = part.substring(1);
                const mentionedUser = mentionedUsers.find((u) => u.fullName === userName);
                if (mentionedUser) {
                    return (
                        <Link
                            key={index}
                            to={`/tasks?userId=${mentionedUser._id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                color: '#2196f3',
                                fontWeight: 'bold',
                                textDecoration: 'none',
                            }}
                        >
                            {part}
                        </Link>
                    );
                }
            }
            return part;
        });
    };

    return (
        <Card className="comments comments-box">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="header-title mb-3">Comments</h4>
                    <div className="comment-dropdowns">
                        <Dropdown
                            title={
                                <span>
                                    Module: <b>{moduleFilter ? camelCaseToText(moduleFilter) : 'All'}</b>
                                </span>
                            }
                            onSelect={handleModuleFilterChange}
                            style={{ marginRight: '10px' }}
                        >
                            <Dropdown.Item eventKey="">All</Dropdown.Item>
                            {topics.map((topic) => (
                                <Dropdown.Item eventKey={topic}>{camelCaseToText(topic)}</Dropdown.Item>
                            ))}
                        </Dropdown>

                        <Dropdown
                            title={
                                <span>
                                    Sort By: <b>{camelCaseToText(sortBy)}</b>
                                </span>
                            }
                            onSelect={handleSortByChange}
                        >
                            <Dropdown.Item eventKey="latest">Latest</Dropdown.Item>
                            <Dropdown.Item eventKey="earliest">Earliest</Dropdown.Item>
                        </Dropdown>
                    </div>
                </div>

                {comments.length ? (
                    <Row>
                        <Col className="comments-msg-area">
                            <ul className="conversation-list comments-list" style={{ direction: i18n.dir() }}>
                                {/* comments */}
                                {comments.map((comment: Comment, index: number) => (
                                    <li key={comment._id} className="conversation-text">
                                        <div className="ctext-wrap">
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={<Tooltip id="copy">{isCopied ? 'Copied to clipboard' : 'Copy to clipboard'}</Tooltip>}
                                            >
                                                <i className="mdi mdi-content-copy comment-icon copy" onClick={() => onCopyComment(comment)}></i>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="bottom" overlay={<Tooltip id="delete">Delete Comment</Tooltip>}>
                                                <i className="mdi mdi-delete comment-icon delete" onClick={() => onDeleteComment(comment._id)}></i>
                                            </OverlayTrigger>
                                            <div>
                                                <b>
                                                    {comment.user?.firstName} {comment.user?.lastName}
                                                </b>{' '}
                                                {comment.topic && (
                                                    <span className={'badge noti-icon-badge ' + getBadgeClass(comment)}>{comment.topic}</span>
                                                )}
                                            </div>
                                            {comment.ref.task && <div className="ref-task">{(comment.ref.task as Task).text}</div>}
                                            <p>
                                                {comment.text.split('\n').map((line, i) => (
                                                    <div key={i}>{renderTextWithMentions(line, comment.mentionedUsers)}</div>
                                                ))}
                                            </p>
                                            <p>
                                                <i>{formatDate(comment.createdAt, false, false)}</i>
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </Col>
                    </Row>
                ) : null}

                {/* add comments */}
                <Row className="mt-2">
                    <Col>
                        <div className="border rounded">
                            <MentionTextarea
                                value={newComment}
                                onChange={commentTextChange}
                                placeholder="Write a comment here... (Type @ to mention someone and create a task)"
                                rows={3}
                                users={users}
                                textAreaRef={textAreaRef}
                            />
                            <div className="p-2 bg-light d-flex justify-content-between align-items-center">
                                <FormInput
                                    label=""
                                    type="select"
                                    name="topic"
                                    value={topic}
                                    placeholder="Status"
                                    options={[{ label: 'Select topic', value: '' }].concat(
                                        topics.map((t) => ({
                                            value: t,
                                            label: camelCaseToText(t),
                                        })),
                                    )}
                                    onSelected={onSelected}
                                    // onInputChange={onInputChange}
                                />
                                <button type="submit" className="btn btn-sm btn-success" onClick={addCommentClicked}>
                                    <i className="mdi mdi-send me-1"></i>Submit
                                </button>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default Comments;
